<template>
  <AdminAppLayout title="HRDOS | Dashboard">
    <div class="dash">

      <!-- Page Header -->
      <div class="dash-header">
        <h4 class="dash-title">Dashboard</h4>
        <p class="dash-sub">Overview of registrations, leave trends, and attendance across all offices.</p>
      </div>

      <p v-if="dtrFetchError" class="dtr-error">{{ dtrFetchError }}</p>

      <!-- KPI Row -->
      <div class="kpi-grid">
        <div class="kpi-card kpi-accent-blue">
          <div class="kpi-icon"><i class="material-icons">person_add</i></div>
          <div class="kpi-body">
            <p class="kpi-value">{{ summary.pendingAccounts }}</p>
            <p class="kpi-label">Pending Registrations</p>
          </div>
          <router-link to="/employees-signup-requests" class="kpi-link">
            Review <i class="material-icons">arrow_forward</i>
          </router-link>
        </div>

        <div class="kpi-card kpi-accent-purple">
          <div class="kpi-icon"><i class="material-icons">event_available</i></div>
          <div class="kpi-body">
            <p class="kpi-value">{{ summary.pendingLeaves }}</p>
            <p class="kpi-label">Pending Leave Applications</p>
          </div>
          <router-link to="/leave-management" class="kpi-link">
            Review <i class="material-icons">arrow_forward</i>
          </router-link>
        </div>

        <div class="kpi-card kpi-accent-red">
          <div class="kpi-icon"><i class="material-icons">event_busy</i></div>
          <div class="kpi-body">
            <p class="kpi-value">{{ absences.total }}</p>
            <p class="kpi-label">Absences This Month</p>
          </div>
          <div class="kpi-delta" :class="deltaClass(absences.total_delta, true)">
            {{ deltaLabel(absences.total_delta) }} vs last month
          </div>
        </div>

        <div class="kpi-card kpi-accent-orange">
          <div class="kpi-icon"><i class="material-icons">report_problem</i></div>
          <div class="kpi-body">
            <p class="kpi-value">{{ absences.unexcused }}</p>
            <p class="kpi-label">Unexcused Absences</p>
          </div>
          <div class="kpi-delta" :class="deltaClass(absences.unexcused_delta, true)">
            {{ deltaLabel(absences.unexcused_delta) }} vs last month
          </div>
        </div>

        <div class="kpi-card kpi-accent-green">
          <div class="kpi-icon"><i class="material-icons">verified</i></div>
          <div class="kpi-body">
            <p class="kpi-value">{{ absences.excused }}</p>
            <p class="kpi-label">Excused Absences</p>
          </div>
          <div class="kpi-delta" :class="deltaClass(absences.excused_delta, false)">
            {{ deltaLabel(absences.excused_delta) }} vs last month
          </div>
        </div>
      </div>
      <p class="kpi-footnote">Weekday rows with no logged time-in/out &middot; unexcused = no matching approved leave application or DTR dispute</p>

      <!-- Insights -->
      <InsightsPanel
        :insights="insights"
        :loading="loadingInsights"
        caption="Auto-generated from the charts below — same numbers, read for you"
      />

      <!-- Charts Row -->
      <div class="chart-grid">
        <div class="chart-card">
          <div class="chart-card-head">
            <h6 class="chart-title">Monthly Leave Trends</h6>
            <p class="chart-caption">Applications by month and leave type</p>
          </div>

          <div v-show="chartLoader" class="content-skeleton">
            <div class="skeleton"></div>
          </div>
          <div v-show="!chartLoader" class="chart-body">
            <canvas id="leaveChartCanvas"></canvas>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-card-head">
            <h6 class="chart-title">Lateness &amp; Undertime</h6>
            <p class="chart-caption">Monthly average, all offices combined</p>
          </div>
          <div class="chart-body">
            <canvas ref="lineCanvas"></canvas>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-card-head">
            <h6 class="chart-title">Absence Trend</h6>
            <p class="chart-caption">Excused vs. unexcused, last 6 months</p>
          </div>
          <div class="chart-body">
            <canvas ref="absenceCanvas"></canvas>
          </div>
        </div>
      </div>

      <!-- Office Ranking (full width) -->
      <div class="chart-card chart-card-wide">
        <div class="chart-card-head">
          <h6 class="chart-title">Offices Ranked by Average Lateness</h6>
          <p class="chart-caption">Average late minutes per employee-day, last 3 months</p>
        </div>
        <div class="chart-body tall">
          <canvas ref="barCanvas"></canvas>
        </div>
      </div>

    </div>
  </AdminAppLayout>
</template>

<style scoped>
.dash {
  max-width: 1280px;
  margin: 0 auto;
  padding-bottom: 30px;
}

/* ---- Header ---- */
.dash-header { margin-bottom: 22px; }
.dash-title { margin: 0; font-weight: 700; color: #1e293b; font-size: 1.6rem; }
.dash-sub { margin: 4px 0 0; color: #64748b; font-size: 0.95rem; }

.dtr-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 0.88rem;
}

/* ---- KPI Row ---- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin-bottom: 8px;
}
.kpi-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
}
.kpi-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kpi-icon .material-icons { font-size: 22px; color: #fff; }
.kpi-accent-blue .kpi-icon   { background: linear-gradient(135deg, #60a5fa, #2563eb); }
.kpi-accent-purple .kpi-icon { background: linear-gradient(135deg, #c084fc, #7c3aed); }
.kpi-accent-red .kpi-icon    { background: linear-gradient(135deg, #f87171, #dc2626); }
.kpi-accent-orange .kpi-icon { background: linear-gradient(135deg, #fb923c, #ea580c); }
.kpi-accent-green .kpi-icon  { background: linear-gradient(135deg, #34d399, #059669); }

.kpi-body { flex: 1; }
.kpi-value { font-size: 1.9rem; font-weight: 700; margin: 0; color: #1e293b; line-height: 1.15; }
.kpi-label { font-size: 0.83rem; color: #64748b; margin: 2px 0 0; }

.kpi-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #2563eb;
  text-decoration: none;
}
.kpi-link .material-icons { font-size: 16px; }

.kpi-delta { font-size: 0.8rem; }
.kpi-delta.good { color: #059669; }
.kpi-delta.bad { color: #dc2626; }
.kpi-delta.neutral { color: #94a3b8; }

.kpi-footnote { font-size: 0.8rem; color: #94a3b8; margin: 10px 0 26px; }

/* ---- Charts ---- */
.chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}
.chart-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}
.chart-card-wide { margin-bottom: 20px; }
.chart-card-head { margin-bottom: 16px; }
.chart-title { margin: 0; font-size: 1rem; font-weight: 600; color: #1e293b; }
.chart-caption { margin: 2px 0 0; font-size: 0.82rem; color: #94a3b8; }
.chart-body { position: relative; height: 320px; }
.chart-body.tall { height: 380px; }

canvas {
  width: 100% !important;
  height: 100% !important;
}

.content-skeleton .skeleton {
  width: 100%;
  height: 320px;
  border-radius: 10px;
  background: #f6f7f8;
  background-image: linear-gradient(to right, #f6f7f8 0%, #edeef1 20%, #f6f7f8 40%, #f6f7f8 100%);
  background-repeat: no-repeat;
  background-size: 800px 100%;
  animation-duration: 1.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeholderShimmer;
  animation-timing-function: linear;
}

@keyframes placeholderShimmer {
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
}

@media (max-width: 900px) {
  .chart-grid { grid-template-columns: 1fr; }
}
</style>

<script>
import { api } from "@/axios";
import Chart from 'chart.js/auto';
import AdminAppLayout from "../layout/AdminAppLayout.vue";
import InsightsPanel from "@/components/InsightsPanel.vue";

export default {
  name: "DashboardView",
  components: {
    AdminAppLayout,
    InsightsPanel,
  },
  data() {
    return {
      // Leave trends / pending accounts
      chartInstance: null,
      trendsData: [],
      chartLoader: true,
      summary: {
        pendingLeaves: 0,
        pendingAccounts: 0
      },

      // DTR analytics
      insights: [],
      loadingInsights: true,
      lateness: { months: [], late: [], undertime: [] },
      offices: [],
      absences: {
        total: 0,
        unexcused: 0,
        excused: 0,
        total_delta: 0,
        unexcused_delta: 0,
        excused_delta: 0,
      },
      absenceTrend: { months: [], excused: [], unexcused: [] },
      dtrFetchError: "",
      lineChart: null,
      barChart: null,
      absenceChart: null,
    };
  },
  methods: {
    async fetchLeaveTrends() {
      this.chartLoader = true;

      await api.get('/dashboard/leave-trends')
        .then((res) => {
          this.trendsData = res.data;
          this.renderChart();
        })
        .catch((err) => {
          console.error("Error fetching leave trends:", err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to load chart data', classes: 'red rounded'});
          }
        })
        .finally(() => {
          this.chartLoader = false;
        });
    },

    async fetchDashboardSummary() {
      await api.get('/dashboard/summary')
        .then((res) => {
          this.summary.pendingLeaves = res.data.pending_leaves;
          this.summary.pendingAccounts = res.data.pending_accounts;
        })
        .catch((err) => {
          console.error("Error fetching summary counts:", err);
        });
    },

    renderChart() {
      const canvas = document.getElementById('leaveChartCanvas');
      if (!canvas) return;

      const ctx = canvas.getContext('2d');

      // Extract dynamic month names for the X-axis
      const labels = this.trendsData.map(item => item.month);

      // Map values, defaulting to 0 if none exist for that month
      const vacationData = this.trendsData.map(item => item['Vacation Leave'] || 0);
      const sickData = this.trendsData.map(item => item['Sick Leave'] || 0);
      const maternityData = this.trendsData.map(item => item['Maternity Leave'] || 0);

      // Destroy previous instance to prevent hover overlapping issues
      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'Vacation',
              data: vacationData,
              backgroundColor: '#26a69a',
              borderRadius: 4
            },
            {
              label: 'Sick',
              data: sickData,
              backgroundColor: '#ef5350',
              borderRadius: 4
            },
            {
              label: 'Maternity',
              data: maternityData,
              backgroundColor: '#ab47bc',
              borderRadius: 4
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                font: {
                  family: "'Inter', sans-serif",
                  size: 12
                },
                boxWidth: 14
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              padding: 10,
              backgroundColor: 'rgba(40, 53, 147, 0.9)'
            }
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false }
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: {
                color: '#e0e0e0',
                drawBorder: false
              }
            }
          }
        }
      });
    },

    // ---- DTR analytics ----
    async fetchLateness() {
      try {
        const res = await api.get("/hr/dtr/analytics/lateness", { params: { months: 6 } });
        this.lateness = res.data.data;
      } catch (err) {
        console.error(err);
        this.dtrFetchError = err.response?.data?.message ?? "Failed to load the lateness trend.";
      }
    },
    async fetchOffices() {
      try {
        const res = await api.get("/hr/dtr/analytics/offices", { params: { months: 3 } });
        this.offices = res.data.data.offices;
      } catch (err) {
        console.error(err);
        this.dtrFetchError = err.response?.data?.message ?? "Failed to load the office ranking.";
      }
    },
    async fetchAbsences() {
      try {
        const res = await api.get("/hr/dtr/analytics/absences");
        this.absences = res.data.data;
      } catch (err) {
        console.error(err);
        this.dtrFetchError = err.response?.data?.message ?? "Failed to load the absence summary.";
      }
    },
    async fetchAbsenceTrend() {
      try {
        const res = await api.get("/hr/dtr/analytics/absences-trend", { params: { months: 6 } });
        this.absenceTrend = res.data.data;
      } catch (err) {
        console.error(err);
        this.dtrFetchError = err.response?.data?.message ?? "Failed to load the absence trend.";
      }
    },
    async fetchInsights() {
      this.loadingInsights = true;
      try {
        const res = await api.get("/hr/dtr/analytics/insights");
        this.insights = res.data.data;
      } catch (err) {
        console.error(err);
        // Insights are a nice-to-have on top of the charts — fail quietly here,
        // dtrFetchError above already covers the charts themselves.
      } finally {
        this.loadingInsights = false;
      }
    },
    deltaClass(delta, higherIsBad) {
      if (delta === 0) return "neutral";
      const isBad = higherIsBad ? delta > 0 : delta < 0;
      return isBad ? "bad" : "good";
    },
    deltaLabel(delta) {
      if (delta === 0) return "— same as";
      return (delta > 0 ? "▲ " : "▼ ") + Math.abs(delta);
    },
    renderLineChart() {
      const canvas = this.$refs.lineCanvas;
      if (!canvas) return;
      if (this.lineChart) this.lineChart.destroy();
      this.lineChart = new Chart(canvas, {
        type: "line",
        data: {
          labels: this.lateness.months,
          datasets: [
            {
              label: "Late (min)",
              data: this.lateness.late,
              borderColor: "#2a78d6",
              backgroundColor: "rgba(42,120,214,0.1)",
              tension: 0.3,
              fill: true,
              pointRadius: 3,
            },
            {
              label: "Undertime (min)",
              data: this.lateness.undertime,
              borderColor: "#eb6834",
              backgroundColor: "rgba(235,104,52,0.1)",
              tension: 0.3,
              fill: true,
              pointRadius: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: { legend: { position: "bottom", labels: { boxWidth: 14 } } },
          scales: {
            y: { beginAtZero: true, ticks: { callback: (v) => v + "m" } },
          },
        },
      });
    },
    renderBarChart() {
      const canvas = this.$refs.barCanvas;
      if (!canvas) return;
      if (this.barChart) this.barChart.destroy();
      const sorted = [...this.offices].sort((a, b) => b.value - a.value);
      this.barChart = new Chart(canvas, {
        type: "bar",
        data: {
          labels: sorted.map((o) => o.name),
          datasets: [
            {
              label: "Avg. late (min)",
              data: sorted.map((o) => o.value),
              backgroundColor: "#2a78d6",
              borderRadius: 4,
              maxBarThickness: 22,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { beginAtZero: true, ticks: { callback: (v) => v + "m" } },
          },
        },
      });
    },
    renderAbsenceChart() {
      const canvas = this.$refs.absenceCanvas;
      if (!canvas) return;
      if (this.absenceChart) this.absenceChart.destroy();
      this.absenceChart = new Chart(canvas, {
        type: "bar",
        data: {
          labels: this.absenceTrend.months,
          datasets: [
            {
              label: "Excused",
              data: this.absenceTrend.excused,
              backgroundColor: "#34d399",
              borderRadius: 4,
            },
            {
              label: "Unexcused",
              data: this.absenceTrend.unexcused,
              backgroundColor: "#f87171",
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom", labels: { boxWidth: 14 } } },
          scales: {
            x: { stacked: true, grid: { display: false } },
            y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
          },
        },
      });
    },
  },
  async mounted() {
    // Leave trends + pending accounts (independent of DTR analytics)
    this.fetchLeaveTrends();
    this.fetchDashboardSummary();

    // DTR analytics — fetch together, then render once data is in
    await Promise.all([
      this.fetchLateness(),
      this.fetchOffices(),
      this.fetchAbsences(),
      this.fetchAbsenceTrend(),
      this.fetchInsights(),
    ]);
    this.$nextTick(() => {
      this.renderLineChart();
      this.renderBarChart();
      this.renderAbsenceChart();
    });
  },
  beforeUnmount() {
    // Prevent memory leaks when navigating away from the layout
    if (this.chartInstance) this.chartInstance.destroy();
    if (this.lineChart) this.lineChart.destroy();
    if (this.barChart) this.barChart.destroy();
    if (this.absenceChart) this.absenceChart.destroy();
  }
};
</script>
